import { RootPages } from 'integrations/types';
import type { AppLocation } from 'router/declarative';

type IntegrationNavigationInput = {
  id: string | null;
  path?: string | null;
};

type IntegrationNavigationResult = AppLocation<'/integrations'>;

/**
 * Converts integration id and path to a declarative router location.
 *
 * Examples:
 * - { id: null } → { route: '/integrations', params: {} }
 * - { id: 'aws-v3' } → { route: '/integrations/:integrationId', params: { integrationId: 'aws-v3' } }
 * - { id: 'aws-v3', path: 'import/configure' } → { route: '/integrations/:integrationId/import/configure', params: { integrationId: 'aws-v3' } }
 */
export const toIntegrationLocation = ({
  id,
  path,
}: IntegrationNavigationInput): IntegrationNavigationResult => {
  if (path === RootPages.HISTORY) {
    return {
      route: '/integrations/history',
      params: {},
    };
  }
  if (path === RootPages.NEW_DASHBOARD) {
    return {
      route: '/integrations/dashboard',
      params: {},
    };
  }
  // Dashboard (no integration selected)
  if (!id) {
    return {
      route: '/integrations',
      params: {},
    };
  }

  // Integration overview (no specific path)
  if (!path) {
    return {
      route: '/integrations/:integrationId',
      params: { integrationId: id },
    };
  }

  // Build the full route by appending the path
  const route = `/integrations/:integrationId/${path}` as const;

  return {
    route,
    params: { integrationId: id },
  } as IntegrationNavigationResult;
};
